import dynamic from 'next/dynamic';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { ReactElement, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { ICookiesPopupProps } from '../../components/common/CookiesPopUp';
import AccordionList, {
  IAccordionListItem,
} from '../../components/common/blocks/AccordionList';
import { ICallToActionProps } from '../../components/common/blocks/CallToAction';
import ReviewsCarousel, {
  IReviewsCarouselProps,
} from '../../components/common/blocks/ReviewsCarousel';
import TextblocksWithIcon, {
  ITextblocksWithIconProps,
} from '../../components/common/blocks/TextblocksWithIcon';
import TrustFacts, {
  ITrustFactsProps,
} from '../../components/common/blocks/TrustFacts';
import { ITrustedByProps } from '../../components/common/blocks/TrustedBy';
import { IFooterProps } from '../../components/common/layout/Footer';
import Layout from '../../components/common/layout/Layout';
import { INavBarProps } from '../../components/common/layout/NavBar';
import { IContactProps } from '../../components/contact-us/Contact';
import { SellingPoints } from '../../components/country';
import { ISellingPoints } from '../../components/country/SellingPoints';
import { Hero } from '../../components/home';
import { IHeroComponentProps } from '../../components/home/Hero';
import { PageJSONLDSchema } from '../../models/app.models';
import {
  IBusinessKeyValuesComponent,
  IContactMethod,
  IContactUsForm,
  ICustomerReview,
  ICustomerReviewsComponent,
  ICustomerReviewsMeta,
  ILayoutPageProps,
  IMedia,
  ITextBlockWithIcon,
  ITrademarkMonitoringPage,
  ITrustedByComponent,
  ITrustFacts,
} from '../../models/strapi.models';
import { fetchStrapi } from '../../services/api.service';
import { getRoutes } from '../../shared/useBuild';
import {
  buildApiKey,
  getCommonTranslations,
  getContactMethods,
  isUndefinedOrEmpty,
  populateCookiesPopUpProps,
  populateFooterProps,
  populateNavBarProps,
  scrollToElement,
} from '../../shared/useCommon';
import {
  generateCustomerReviewsSchema,
  generateFaqsSchema,
  generateOrganizationSchema,
  generateWebsiteSchema,
} from '../../shared/useJSONLDSchema';
import {
  flatStrapiCollection,
  flatStrapiObject,
  formatDate,
  getAlternateLinks,
  getCanonicalLink,
  getContentLanguageCode,
  getImageSrcWithFallback,
  getTranslation,
  keepObjectProperties,
} from '../../shared/useUtils';
import { NextPageWithLayout } from '../_app';

export interface ITrademarkMonitoringPageProps extends ILayoutPageProps {
  hero: IHeroComponentProps;
  introductionTitle: string;
  introductionDescription: string;
  introductionImage: IMedia;
  faqsTitle: string;
  faqs: IAccordionListItem[];
  businessKeyValues: IBusinessKeyValuesComponent;
  customerReviews: ICustomerReviewsComponent;
  reviews: ICustomerReview[];
  trustedBy: ITrustedByComponent;
  contactUsForm: IContactUsForm;
  contactUsFormSuccessMessage: string;
  contactUsFormErrorMessage: string;
  contactMethods: IContactMethod[];
  alternativeLinks: { hrefLang: string; href: string }[];
  canonicalLink: string;
  howItWorks: ITextblocksWithIconProps;
  customerReviewsMeta: ICustomerReviewsMeta;
  customerReviewsTopTipStart: string;
  customerReviewsTopTipEnd: string;
  customerReviewsBottomTip: string;
  callToAction: ICallToActionProps;
  trustFactsProps: ITrustFactsProps;
}

const TrademarkMonitoringPage: NextPageWithLayout<
  ITrademarkMonitoringPageProps
> = (props: ITrademarkMonitoringPageProps) => {
  const router = useRouter();
  const [hydrated, setHydrated] = useState(false);

  const heroProps: IHeroComponentProps = {
    ...props.hero,
    secondaryButtonAction: () => scrollToElement('contact'),
  };

  const contactProps: IContactProps = {
    contactMethods: props.contactMethods.map((x) => ({
      type: x.type,
      label: getTranslation(x.labelTranslations, router.locale || 'en')
        .text as string,
      value:
        x.valueTranslations && x.valueTranslations.length > 0
          ? (getTranslation(x.valueTranslations, router.locale || 'en')
              .text as string)
          : undefined,
    })),
    titleTagName: 'h2',
    formLabels: {
      title: props.contactUsForm.title,
      subTitle: props.contactUsForm.subTitle,
      name: props.contactUsForm.nameLabel,
      email: props.contactUsForm.emailLabel,
      message: props.contactUsForm.messageLabel,
      submit: props.contactUsForm.submitLabel,
      submitSuccessMessage: props.contactUsFormSuccessMessage,
      submitErrorMessage: props.contactUsFormErrorMessage,
    },
  };

  const businessKeyValueProps: ISellingPoints = {
    title: props.businessKeyValues.title,
    description: props.businessKeyValues.description,
    sellingPointsItems: [
      {
        title: props.businessKeyValues.keyValueOneTitle,
        description: props.businessKeyValues.keyValueOneDescription,
        iconPath: '/assets/images/icons/ExpertAgentIcon.svg',
      },
      {
        title: props.businessKeyValues.keyValueTwoTitle,
        description: props.businessKeyValues.keyValueTwoDescription,
        iconPath: '/assets/images/icons/WorldWideServicesIcon.svg',
      },
      {
        title: props.businessKeyValues.keyValueThreeTitle,
        description: props.businessKeyValues.keyValueThreeDescription,
        iconPath: '/assets/images/icons/NoHiddenChargesIcon.svg',
      },
      {
        title: props.businessKeyValues.keyValueFourTitle,
        description: props.businessKeyValues.keyValueFourDescription,
        iconPath: '/assets/images/icons/FreeConsultIcon.svg',
      },
    ],
  };

  const reviewsCarouselProps: IReviewsCarouselProps = {
    averageScore: props.customerReviewsMeta.average,
    totalReviews: props.customerReviewsMeta.numberOfReviews,
    topTipStart: props.customerReviewsTopTipStart,
    topTipEnd: props.customerReviewsTopTipEnd,
    title: props.customerReviews.title,
    description: props.customerReviews.description,
    bottomTip: props.customerReviewsBottomTip,
    defaultLinkPath: props.customerReviews.buttonPath,
    reviews: props.reviews.map((review) => ({
      text: review.content,
      username: review.customerName,
      score: 5,
      date: formatDate(review.date),
      link: review.link,
    })),
  };

  const trustedByProps: ITrustedByProps = {
    title: props.trustedBy.title,
    companies: props.trustedBy.logos.map((x) => ({
      company: 'company',
      image: (
        <Image
          src={x.url as string}
          fill
          sizes="175px"
          alt={x.alternativeText || ''}
        />
      ),
    })),
  };

  const callToActionProps: ICallToActionProps = {
    title: props.callToAction.title,
    buttons: props.callToAction.buttons,
  };

  const TrustedByComponent = dynamic(
    () => import('../../components/common/blocks/TrustedBy')
  );
  const ContactComponent = dynamic(
    () => import('../../components/contact-us/Contact')
  );
  const CallToActionComponent = dynamic(
    () => import('../../components/common/blocks/CallToAction')
  );

  useEffect(() => {
    setHydrated(true);
  }, []);

  return (
    <div className="page-container">
      <Head>
        <title>{props.meta.title}</title>
        <meta name="description" content={props.meta.description} />
        <meta name="keywords" content={props.meta.keywords} />
        <link rel="canonical" href={props.canonicalLink} />
        {props.alternativeLinks.map((x, i) => (
          <link key={i} rel="alternate" hrefLang={x.hrefLang} href={x.href} />
        ))}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(props.pageSchema.nodes),
          }}
        />
      </Head>

      <div className="page-content-section">
        <div className="page-content-block">
          <Hero {...heroProps} />
        </div>
        <div className="page-content-block">
          <TrustFacts {...props.trustFactsProps} />
        </div>
      </div>

      <div className="page-content-section background-light">
        <div className="page-content-block">
          <div className="two-columns">
            <div className="min-height-image-container">
              <Image
                src={getImageSrcWithFallback(props.introductionImage, 'large')}
                alt={
                  props.introductionImage.alternativeText ||
                  props.introductionTitle
                }
                fill
                sizes="(max-width: 768px) 100vw, 75vw"
                quality={90}
                style={{ objectFit: 'contain' }}
              />
            </div>
            <div className="paragraphs-lighter">
              <h2>{props.introductionTitle}</h2>
              <ReactMarkdown>{props.introductionDescription}</ReactMarkdown>
            </div>
          </div>
        </div>
      </div>

      <div className="page-content-section">
        <div className="page-content-block">
          <TextblocksWithIcon {...props.howItWorks} />
        </div>
      </div>

      <div className="page-content-section background-light">
        <div className="page-content-block">
          <SellingPoints {...businessKeyValueProps} />
        </div>
      </div>

      <div className="page-content-section">
        <div className="page-content-block">
          {hydrated && <ReviewsCarousel {...reviewsCarouselProps} />}
        </div>
      </div>

      <div className="page-content-section background-light">
        <div className="page-content-block">
          <TrustedByComponent {...trustedByProps} />
        </div>
      </div>

      {props.faqs && props.faqs.length > 0 && (
        <div className="page-content-section">
          <div className="page-content-block">
            <AccordionList title={props.faqsTitle || ''} items={props.faqs} />
          </div>
        </div>
      )}

      <div className="page-content-section is-narrower">
        <div className="page-content-block" id="contact">
          <ContactComponent {...contactProps} />
        </div>
      </div>

      <div className="page-content-section is-full-width">
        <div className="page-content-block" id="services">
          <CallToActionComponent {...callToActionProps} />
        </div>
      </div>
    </div>
  );
};

TrademarkMonitoringPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <Layout
      locale={page.props.locale}
      navBarProps={page.props.navBarProps}
      cookiesPopUpProps={page.props.cookiesPopUpProps}
      footerProps={page.props.footerProps}
      contentLanguageCode={getContentLanguageCode(page.props.locale)}>
      {page}
    </Layout>
  );
};

export async function getStaticProps(
  context: any
): Promise<{ props: ITrademarkMonitoringPageProps }> {
  const appRoutes = await getRoutes();

  const pageUrlParams = new URLSearchParams({
    'populate[0]': 'meta',
    'populate[1]': 'hero',
    'populate[2]': 'hero.background',
    'populate[3]': 'introductionTitle',
    'populate[4]': 'introductionDescription',
    'populate[5]': 'introductionImage',
    'populate[6]': 'businessKeyValues',
    'populate[7]': 'howItWorksTitle',
    'populate[8]': 'howItWorks',
    'populate[9]': 'howItWorks.icon',
    'populate[10]': 'trustedBy',
    'populate[11]': 'trustedBy.logos',
    'populate[12]': 'customerReviews',
    'populate[13]': 'faqsTitle',
    'populate[14]': 'faqs',
    'populate[15]': 'contactUsForm',
    'populate[16]': 'contactUsFormSuccessMessage',
    'populate[17]': 'contactUsFormErrorMessage',
    'populate[18]': 'breadcrumbAncestors',
    'populate[19]': 'callToAction',
    'populate[20]': 'callToAction.buttons',
  });

  const customerReviewsUrlParams = {
    locale: context.locale,
  };

  const page = (
    await fetchStrapi<any>(
      `/trademark-monitoring-page`,
      pageUrlParams,
      buildApiKey
    )
  ).data.attributes as ITrademarkMonitoringPage;
  const contactMethods = await getContactMethods();
  const navBarProps: INavBarProps = await populateNavBarProps(context.locale);
  const cookiesPopUpProps: ICookiesPopupProps = await populateCookiesPopUpProps(
    context.locale
  );
  const footerProps: IFooterProps = await populateFooterProps(context.locale);
  const commonTranslations = await getCommonTranslations();
  const reviews = flatStrapiCollection(
    await fetchStrapi<any>(
      `/customer-reviews`,
      customerReviewsUrlParams,
      buildApiKey
    )
  ) as ICustomerReview[];
  const trustedBy: ITrustedByComponent = getTranslation(
    page.trustedBy,
    context.locale
  );
  trustedBy.logos = flatStrapiCollection(trustedBy.logos as any).map((x) =>
    keepObjectProperties<IMedia>(x, ['url', 'alternativeText', 'name'])
  );
  const customerReviews: ICustomerReviewsComponent = getTranslation(
    page.customerReviews,
    context.locale
  );
  const customerReviewsMeta = (
    await fetchStrapi<any>(`/customer-reviews-meta`, undefined, buildApiKey)
  ).data.attributes as ICustomerReviewsMeta;

  // Check if customer reviews title contains placecholders. Example: "Average rating: {{average}} based on {{numberOfReviews}} reviews (Best rating: {{bestRating}})"
  if (
    customerReviews.title.includes('{{') &&
    customerReviews.title.includes('}}')
  ) {
    customerReviews.title = customerReviews.title.replace(
      '{{average}}',
      customerReviewsMeta.average.toString()
    );
    customerReviews.title = customerReviews.title.replace(
      '{{numberOfReviews}}',
      customerReviewsMeta.numberOfReviews.toString()
    );
    customerReviews.title = customerReviews.title.replace(
      '{{bestRating}}',
      customerReviewsMeta.bestRating.toString()
    );
  }

  // Prepare Hero
  const heroData = getTranslation(page.hero, context.locale);
  const heroProps: IHeroComponentProps = {
    title: heroData.title,
    description: heroData.description,
    buttonLabel: heroData.buttonLabel,
    buttonPath: heroData.buttonPath || '',
    secondaryButtonLabel: heroData.secondaryButtonLabel,
    backgroundUrl:
      getImageSrcWithFallback(flatStrapiObject(heroData.background), 'full') ||
      '/assets/images/home/map-image.png',
    titleHighlightedText: heroData.highlightedTitleText,
    backgroundAlt:
      flatStrapiObject<any>(heroData.background).alternativeText ||
      'Hero Background Image',
  };

  // Populate breadcrumbs
  if (page.breadcrumbAncestors && page.breadcrumbAncestors.length > 0) {
    navBarProps.breadcrumbAncestors = page.breadcrumbAncestors?.filter(
      (x) => x.language === context.locale
    );
  }

  navBarProps.breadcrumbsHomePageDisplayName = getTranslation(
    commonTranslations.homePageName,
    context.locale
  ).text as string;
  navBarProps.breadcrumbsCurrentPageDisplayName = heroData.title as string;

  const faqsTitle =
    page.faqsTitle && page.faqsTitle.length > 0
      ? (getTranslation(page.faqsTitle, context.locale).text as string)
      : (getTranslation(commonTranslations.faqsTitle, context.locale)
          .text as string);
  const faqs = page?.faqs
    ?.filter((x) => x.language === context.locale)
    .map((x) => ({
      title: x.question,
      content: x.answer,
    })) as IAccordionListItem[];

  const websiteSchema = generateWebsiteSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string
  );
  const organizationSchema = await generateOrganizationSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string,
    customerReviewsMeta
  );
  const faqsSchema = generateFaqsSchema(
    faqs.map((x) => ({ question: x.title, answer: x.content }))
  );
  const customerReviewsSchema = generateCustomerReviewsSchema(
    reviews,
    customerReviewsMeta
  );

  const pageSchema: PageJSONLDSchema = {
    nodes: [
      ...websiteSchema,
      ...organizationSchema,
      ...faqsSchema,
      ...customerReviewsSchema,
    ],
  };

  const meta = getTranslation(page.meta, context.locale);
  const alternativeLinks = getAlternateLinks(
    '/trademark-monitoring',
    appRoutes
  );
  const canonicalLink = getCanonicalLink(context.locale || 'en', meta.slug);

  const contactUsFormSuccessMessage = isUndefinedOrEmpty(
    page.contactUsFormSuccessMessage
  )
    ? (getTranslation(
        commonTranslations.contactUsFormSuccessMessage,
        context.locale
      ).text as string)
    : (getTranslation(page.contactUsFormSuccessMessage, context.locale)
        .text as string);
  const contactUsFormErrorMessage = isUndefinedOrEmpty(
    page.contactUsFormErrorMessage
  )
    ? (getTranslation(
        commonTranslations.contactUsFormErrorMessage,
        context.locale
      ).text as string)
    : (getTranslation(page.contactUsFormErrorMessage, context.locale)
        .text as string);

  const howItWorks: ITextblocksWithIconProps = {
    title: getTranslation(page.howItWorksTitle, context.locale).text as string,
    items: page.howItWorks
      .filter((x) => x.language === context.locale)
      .map((x: ITextBlockWithIcon) => ({
        content: x.content,
        icon: flatStrapiObject(x.icon as any) as IMedia,
      })),
  };

  const trustFacts: ITrustFacts = getTranslation(
    commonTranslations.trustFactsStrip,
    context.locale
  );

  const trustFactsProps: ITrustFactsProps = {
    locale: context.locale,
    facts: (trustFacts?.facts || []).map((x) => ({
      value: x.value,
      description: x.description,
    })),
  };

  const props: ITrademarkMonitoringPageProps = {
    // Layout
    pageSchema,
    navBarProps,
    cookiesPopUpProps,
    footerProps,
    // Page
    locale: context.locale,
    meta,
    hero: heroProps,
    introductionTitle:
      getTranslation(page.introductionTitle, context.locale).text || '-',
    introductionDescription:
      getTranslation(page.introductionDescription, context.locale).text || '-',
    introductionImage: flatStrapiObject(page.introductionImage),
    businessKeyValues: getTranslation(page.businessKeyValues, context.locale),
    trustedBy,
    customerReviews,
    reviews,
    contactMethods,
    contactUsForm: getTranslation(page.contactUsForm, context.locale),
    contactUsFormSuccessMessage,
    contactUsFormErrorMessage,
    canonicalLink,
    alternativeLinks,
    howItWorks,
    faqsTitle,
    faqs,
    customerReviewsMeta,
    customerReviewsTopTipStart: getTranslation(
      commonTranslations.customerReviewsTopTipStart,
      context.locale
    ).text as string,
    customerReviewsTopTipEnd: getTranslation(
      commonTranslations.customerReviewsTopTipEnd,
      context.locale
    ).text as string,
    customerReviewsBottomTip: getTranslation(
      commonTranslations.customerReviewsBottomTip,
      context.locale
    ).text as string,
    callToAction: getTranslation(page.callToAction, context.locale),
    trustFactsProps,
  };

  return {
    props,
  };
}

export default TrademarkMonitoringPage;
